import React, { useState, useRef, useEffect, useImperativeHandle, forwardRef } from 'react';
import { FiEdit2, FiCheck, FiX } from 'react-icons/fi';
import { usePlanName } from '../PlanNameContext';
import { useHasChanges } from '../HasChangesContext';

// CSS-Animation für den Spinner und Fade-In
const spinnerStyles = `
  @keyframes autosave-spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
  
  @keyframes fadeIn {
    0% { 
      opacity: 0; 
      transform: translateY(-5px); 
    }
    100% { 
      opacity: 1; 
      transform: translateY(0); 
    }
  }
`;

// Style-Tag zum Document hinzufügen (einmalig)
if (typeof document !== 'undefined' && !document.getElementById('autosave-spinner-styles')) {
  const style = document.createElement('style');
  style.id = 'autosave-spinner-styles';
  style.textContent = spinnerStyles;
  document.head.appendChild(style);
}

const PlanNameEditor = forwardRef(({ style = {}, autosaveFunction = null, silentAutosaveFunction = null }, ref) => {
  const { 
    currentPlanName, 
    isEditing, 
    startEditing, 
    stopEditing, 
    updatePlanName 
  } = usePlanName();
  
  const { hasChanges } = useHasChanges();
  
  const [editValue, setEditValue] = useState('');
  const [autosaveEnabled, setAutosaveEnabled] = useState(true);
  const [lastAutosave, setLastAutosave] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const [showSaveConfirmation, setShowSaveConfirmation] = useState(false);
  const inputRef = useRef(null);
  const autosaveIntervalRef = useRef(null);
  const hasChangesRef = useRef(false);
  const saveConfirmationTimeoutRef = useRef(null);

  // Lade Benutzerauswahl für Autosave beim Komponentenstart
  useEffect(() => {
    const userPreference = localStorage.getItem('userAutosavePreference');
    if (userPreference !== null) {
      setAutosaveEnabled(userPreference === 'true');
    }
    // Falls keine Benutzerauswahl vorhanden, bleibt der Standardwert (true)
  }, []);

  // Initialisiere editValue wenn Bearbeitung startet
  useEffect(() => {
    if (isEditing) {
      setEditValue(currentPlanName);
      setTimeout(() => {
        if (inputRef.current) {
          inputRef.current.focus();
          inputRef.current.select();
        }
      }, 0);
    }
  }, [isEditing, currentPlanName]);

  // Aktualisiere hasChanges Ref
  useEffect(() => {
    hasChangesRef.current = hasChanges;
  }, [hasChanges]);

  // Stille Autosave-Funktion (ohne störende Dialoge)
  const handleSilentAutosave = React.useCallback(async () => {
    if (!silentAutosaveFunction || typeof silentAutosaveFunction !== 'function') {
      return;
    }

    setIsSaving(true);
    try {
      const success = await silentAutosaveFunction();
      if (success) {
        setLastAutosave(new Date());
      }
    } catch (error) {
      console.error('Autosave Fehler:', error);
    } finally {
      setIsSaving(false);
    }
  }, [silentAutosaveFunction]);

  // Autosave-Intervall verwalten
  useEffect(() => {
    // Entferne vorhandenes Intervall
    if (autosaveIntervalRef.current) {
      clearInterval(autosaveIntervalRef.current);
      autosaveIntervalRef.current = null;
    }

    // Erstelle neues Intervall wenn autosave aktiviert ist und eine Funktion vorhanden ist
    if (autosaveEnabled && silentAutosaveFunction && typeof silentAutosaveFunction === 'function') {
      autosaveIntervalRef.current = setInterval(() => {
        const currentHasChanges = hasChangesRef.current;
        
        if (currentHasChanges && !isSaving) {
          handleSilentAutosave();
        }
      }, 10000); // 60 Sekunden
    }

    // Cleanup beim Unmount oder wenn sich die Abhängigkeiten ändern
    return () => {
      if (autosaveIntervalRef.current) {
        clearInterval(autosaveIntervalRef.current);
        autosaveIntervalRef.current = null;
      }
    };
  }, [autosaveEnabled, silentAutosaveFunction, handleSilentAutosave, isSaving]);

  const handleAutosaveToggle = () => {
    const newValue = !autosaveEnabled;
    setAutosaveEnabled(newValue);
    
    // Speichere die Benutzerauswahl im localStorage (synchron mit Header.js)
    localStorage.setItem('userAutosavePreference', newValue.toString());
  };

  // Funktion für Ctrl+S Speicher-Bestätigung
  const showCtrlSaveConfirmation = () => {
    // Entferne vorhandenes Timeout
    if (saveConfirmationTimeoutRef.current) {
      clearTimeout(saveConfirmationTimeoutRef.current);
    }
    
    setShowSaveConfirmation(true);
    
    // Nach 5 Sekunden ausblenden
    saveConfirmationTimeoutRef.current = setTimeout(() => {
      setShowSaveConfirmation(false);
      saveConfirmationTimeoutRef.current = null;
    }, 2000);
  };

  // Cleanup beim Unmount
  useEffect(() => {
    return () => {
      if (saveConfirmationTimeoutRef.current) {
        clearTimeout(saveConfirmationTimeoutRef.current);
      }
    };
  }, []);

  // Expose Funktionen nach außen
  useImperativeHandle(ref, () => ({
    showCtrlSaveConfirmation
  }));

  const handleStartEdit = () => {
    startEditing();
  };

  const handleConfirmEdit = () => {
    updatePlanName(editValue);
    stopEditing();
  };

  const handleCancelEdit = () => {
    setEditValue(currentPlanName);
    stopEditing();
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      handleConfirmEdit();
    } else if (e.key === 'Escape') {
      handleCancelEdit();
    }
  };

  const handleBlur = () => {
    // Bestätige Änderungen beim Verlassen des Feldes
    handleConfirmEdit();
  };

  if (isEditing) {
    return (
      <div style={{ 
        display: 'flex', 
        alignItems: 'center', 
        gap: 8,
        ...style 
      }}>
        <input
          ref={inputRef}
          type="text"
          value={editValue}
          onChange={(e) => setEditValue(e.target.value)}
          onKeyDown={handleKeyDown}
          onBlur={handleBlur}
          style={{
            background: 'rgba(255, 255, 255, 0.1)',
            border: '1px solid rgba(255, 255, 255, 0.3)',
            borderRadius: 4,
            color: '#fff',
            padding: '4px 8px',
            fontSize: 18,
            fontWeight: 'normal',
            minWidth: 200,
            maxWidth: 300
          }}
          placeholder="Planname eingeben..."
        />
        <button
          onClick={handleConfirmEdit}
          style={{
            background: 'transparent',
            border: 'none',
            color: '#4caf50',
            cursor: 'pointer',
            padding: 4,
            display: 'flex',
            alignItems: 'center'
          }}
          title="Bestätigen (Enter)"
        >
          <FiCheck size={16} />
        </button>
        <button
          onClick={handleCancelEdit}
          style={{
            background: 'transparent',
            border: 'none',
            color: '#f44336',
            cursor: 'pointer',
            padding: 4,
            display: 'flex',
            alignItems: 'center'
          }}
          title="Abbrechen (Escape)"
        >
          <FiX size={16} />
        </button>
      </div>
    );
  }

  return (
    <div style={{ 
      display: 'flex', 
      alignItems: 'center', 
      gap: 8,
      ...style 
    }}>
      <span 
        style={{ 
          fontSize: 18,
          color: '#ccc',
          cursor: 'pointer',
          padding: '4px 8px',
          borderRadius: 4,
          transition: 'background-color 0.2s'
        }}
        onMouseOver={(e) => {
          e.target.style.backgroundColor = 'rgba(255, 255, 255, 0.1)';
        }}
        onMouseOut={(e) => {
          e.target.style.backgroundColor = 'transparent';
        }}
        onClick={handleStartEdit}
        title="Klicken zum Bearbeiten"
      >
        {currentPlanName || 'Unbenannt'}
      </span>
      
      
      
      <button
        onClick={handleStartEdit}
        style={{
          background: 'transparent',
          border: 'none',
          color: '#999',
          cursor: 'pointer',
          padding: 4,
          display: 'flex',
          alignItems: 'center',
          opacity: 0.7,
          transition: 'opacity 0.2s'
        }}
        onMouseOver={(e) => {
          e.target.style.opacity = 1;
        }}
        onMouseOut={(e) => {
          e.target.style.opacity = 0.7;
        }}
        title="Planname bearbeiten"
      >
        <FiEdit2 size={14} />
      </button>


      {/* Änderungs-Indikator */}
      <div
        style={{
          width: 8,
          height: 8,
          borderRadius: '50%',
          backgroundColor: hasChanges ? '#ff9800' : '#4caf50',
          marginLeft: 4,
          animation: hasChanges ? 'pulse 2s infinite' : 'none',
          transition: 'background-color 0.3s ease'
        }}
        title={hasChanges ? "Ungespeicherte Änderungen (CTRL-S zum sofort speichern)" : "Alle Änderungen gespeichert"}
      />
      
      {/* Ctrl+S Speicher-Bestätigung */}
      {showSaveConfirmation && (
        <div
          style={{
            marginLeft: 8,
            padding: '4px 8px',
            backgroundColor: '#4caf50',
            color: 'white',
            borderRadius: 4,
            fontSize: 12,
            fontWeight: 'bold',
            animation: 'fadeIn 0.3s ease-in-out',
            display: 'flex',
            alignItems: 'center',
            gap: 4
          }}
        >
          ✓ Gespeichert {lastAutosave && `um ${lastAutosave.toLocaleTimeString()}`}
        </div>
      )}
      
      {/* Autosave Switch */}
      <div style={{ 
        display: 'flex', 
        alignItems: 'center', 
        gap: 6, 
        marginLeft: 12 
      }}>
        <span style={{ 
          fontSize: 12, 
          color: '#aaa', 
          whiteSpace: 'nowrap',
          userSelect: 'none'
        }}>
          Autosave
        </span>
        
        {/* Saving Spinner */}
        {isSaving && (
          <div
            className="autosave-spinner"
            style={{
              width: 12,
              height: 12,
              border: '2px solid #666',
              borderTop: '2px solid #4caf50',
              borderRadius: '50%',
              marginLeft: 2,
              animation: 'autosave-spin 1s linear infinite'
            }}
            title="Speichere..."
          />
        )}
        <label
          style={{
            position: 'relative',
            display: 'inline-block',
            width: 36,
            height: 20,
            cursor: 'pointer'
          }}
          title={autosaveEnabled 
            ? `Autosave aktiviert (alle 60s)${lastAutosave ? ` - Letztes Autosave: ${lastAutosave.toLocaleTimeString()}` : ''}` 
            : "Autosave deaktiviert - klicken zum aktivieren"
          }
        >
          <input
            type="checkbox"
            checked={autosaveEnabled}
            onChange={handleAutosaveToggle}
            style={{
              opacity: 0,
              width: 0,
              height: 0
            }}
          />
          <span
            style={{
              position: 'absolute',
              cursor: 'pointer',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: autosaveEnabled ? '#4caf50' : '#666',
              borderRadius: 20,
              transition: 'all 0.3s ease'
            }}
          >
            <span
              style={{
                position: 'absolute',
                content: '',
                height: 16,
                width: 16,
                left: autosaveEnabled ? 18 : 2,
                top: 2,
                backgroundColor: 'white',
                borderRadius: '50%',
                transition: 'left 0.3s ease',
                boxShadow: '0 1px 2px rgba(0,0,0,0.2)'
              }}
            />
          </span>
        </label>
      </div>
    </div>
  );
});

PlanNameEditor.displayName = 'PlanNameEditor';

export default PlanNameEditor;